/* ======================= GAME CORE =======================
   The scratch surface, tension/reveal machinery, card flow, deposit UI and
   session-limit logic. Kept as one module on purpose: the canvas reveal
   detection, the tension loop and finish() share tightly-coupled mutable
   state, and CLAUDE.md records that splitting/perturbing the pixel-sampling
   path has already cost nine debugging rounds once. Split further only with
   the self-tests green. */
import { $, fmt, BUILD_ID, IS_DEMO_BUILD } from "./core";
import { BASE, TIERS, MOONS, FILLERS, ALLSYMS, NAMES } from "./config";
import { symArt } from "./art";
import { takeForced } from "./demo-state";
import { classifyOutcome } from "./outcome";
import * as persist from "./persist";
import { CHAIN_CONFIG, SETTLEMENT, CURRENCIES, setSettlement, isOnchainConfigured, formatUnits_, displayToUnits, unitsToDisplay } from "./chain/config";
import { play as chainPlay, gameBalanceUnits, sessionAllowance, reauthoriseSession, explainChainError, legacyBalanceWei, withdrawLegacy, ChainError } from "./chain/game";
import {
  audioInit, tone, beep, noiseBurst, bellStrike, pingCoin, brass, crash,
  tickSnd, bell, dingDingDing, fanfare, megafanfare, siren, whoosh, womp,
  riser, payClink, coinClink, stinger, grit, scratchSound, scratchStop,
  hbRate, tensionStart, tensionStop, toggleMute, scrapeOn, scrapeOff, resumeAudio
} from "./audio";

/* Restored below from localStorage. `bal` is NOT among them and never will be:
   it is the contract's number for a wallet player and meaningless for a demo
   one, so persisting it would be the phantom-credit bug in durable form. */
const saved=persist.load();
let bal=0, tier=TIERS[0], played=saved.played, totalWon=saved.totalWon,
    best=saved.best, streak=saved.streak;
let cells: any[]=[], winSet: Set<number>|null=null, prize=0, resolved=true, scratching=false, tension=false;
let token="USDG", depAmt=25, depositing=false, dealtLocked=true, demoMode=false;
let demoSession=false, onboardingDemo=false;
/* True while a card's outcome came from the chain. finish() must then NOT
   mutate `bal` itself — the contract already settled, and the balance is
   refreshed from it. Double-counting here would show players money they do
   not have. */
let onchainSettled=false;
/* Did the player actually stake this card?
   A card is dealt at load and on every tier switch purely so the board is not
   empty, and those were never bought. settleAbandoned() credited their prize
   anyway, so a brand-new session with no wallet and no deposit showed
   "Previous card settled: +100 gwei" and a balance to match. */
let cardStaked=false;
let onchainBusy=false;

$("muteBtn").onclick=()=>{
  const m=toggleMute(tension);
  $("muteBtn").textContent=m?"🔇":"🔊";
};


/* ======================= RACK ======================= */
TIERS.forEach(t=>{
  const d=document.createElement("div");
  d.className="mini "+t.cls+(t.id===0?" sel":"");
  const _wr=t.prizes.reduce((a,[,pp])=>a+pp,0), _ev=t.prizes.reduce((a,[x,pp])=>a+x*pp,0);
  d.innerHTML=`${t.tag?`<span class="pop">${t.tag}</span>`:""}<h3>${t.short}</h3>
    <div class="hook">${t.hook}</div>
    <div class="top"><span class="wl">Win up to</span><b>${money(t.top)}</b></div>
    <div class="mo">Pays 1 in ${(1/_wr).toFixed(1)} · RTP ${(_ev/t.price*100).toFixed(1)}%</div>
    <div class="price">${money(t.price)}</div>`;
  d.onclick=()=>{
    audioInit();
    document.querySelectorAll<HTMLElement>(".mini").forEach(m=>m.classList.remove("sel"));d.classList.add("sel");
    tier=t;
    /* Selecting a tier NEVER buys. Tapping the rack used to call attemptBuy(),
       so browsing between cards silently charged for a card each time — the
       $25-vanishes bug. Purchase happens only via Play again / the buy button. */
    if(demoSession){dealDemo();}
    else if(dealtLocked){newCard(false);showLock();}
    else if(resolved||revealedCells.size===0){
      /* unbought preview of the new tier — locked until they choose to buy,
         otherwise switching tiers would hand out free cards */
      newCard(false); dealtLocked=true; showTierLock();
    }
    /* a live, part-scratched card is left alone */
  };
  $("rack").appendChild(d);
});
function attemptBuy(){
  if(demoSession){dealDemo();return;}
  if(isOnchainConfigured()){dealOnchain();return;}
  if(bal>=tier.price)newCard(true);
  else openDeposit(`You need ${money(tier.price)} to play ${tier.short}`);
}

/* ================= ON-CHAIN PLAY =================
   The card is bought and resolved by the contract before anything is drawn.
   The scratch animation is pure theatre over an outcome that is already
   settled and verifiable on-chain — which is the honest version of what the
   local RNG was only pretending to do. */
async function refreshOnchainBalance(){
  const acct=window.__onchain&&window.__onchain.account&&window.__onchain.account();
  if(!acct)return;
  try{
    bal=await gameBalanceUnits(acct);
    renderBal(true);
    unlockIfNeeded();
  }catch(e){ /* a read failure must not wedge the game */ }
}

/**
 * Called by the wallet layer whenever the connected account changes — on
 * connect, on an accountsChanged event, and on reload with a wallet already
 * authorised.
 *
 * Without this the balance was only ever read after a deposit or a play, so a
 * reload showed 0 next to funds that were sitting in the contract the whole
 * time. The chain is the source of truth; the UI has to go and ask.
 */
export async function onWalletAccount(){
  if(!isOnchainConfigured())return;
  const acct=window.__onchain&&window.__onchain.account&&window.__onchain.account();
  if(!acct){ bal=0; renderBal(); return; }
  await refreshOnchainBalance();
  await warnIfSessionKeyUnusable(acct);
  await offerLegacyRecovery(acct);
}

/**
 * Offer to recover a balance stranded in the previous contract.
 *
 * The game moved to a new address for multi-currency support. Balances did not
 * move with it — withdraw() is caller-only, so nobody could move them on a
 * player's behalf — and the old contract still holds exactly what it owes.
 * A returning player would otherwise see zero and have no way to know why.
 */
async function offerLegacyRecovery(acct:string){
  try{
    const wei=await legacyBalanceWei(acct);
    if(wei<=0n)return;
    /* Denominated in ETH regardless of the asset now selected: the old
       contract only ever settled native ETH. */
    const gwei=(Number(wei)/1e9).toLocaleString(undefined,{maximumFractionDigits:3});
    const st=$("legacyBar");
    if(!st)return;
    st.innerHTML=`You have <b>${gwei} gwei</b> in the previous version of the game. `+
      `<button class="chip" id="legacyGo">Withdraw it</button>`;
    st.classList.add("on");
    $("legacyGo").onclick=async()=>{
      const prov=window.__onchain&&window.__onchain.provider&&window.__onchain.provider();
      if(!prov){ window.__onchain&&window.__onchain.connect&&window.__onchain.connect(); return; }
      const b=$("legacyGo") as HTMLButtonElement;
      b.disabled=true; b.textContent="Confirm in your wallet…";
      window.__onchain&&window.__onchain.nudge&&window.__onchain.nudge();
      try{
        await withdrawLegacy(prov,wei);
        st.classList.remove("on");
        toast(`Recovered ${gwei} gwei from the previous game`);
      }catch(e){
        b.disabled=false; b.textContent="Withdraw it";
        toast(explainChainError(e));
      }
    };
  }catch(e){ /* advisory only */ }
}

/**
 * The session key is authorised on-chain against a key held in localStorage.
 * Clearing site data, or a deposit made from a different browser, leaves the
 * allowance pointing at a delegate this browser no longer has — and the only
 * symptom would be every play failing at the relayer. Say so up front instead.
 */
/**
 * Held back until the player is between cards.
 *
 * The renewal sheet is advisory — the session has run low, but nothing is
 * broken yet and the current card is unaffected. Throwing a modal over a card
 * someone is part-way through scratching interrupts the one moment the game
 * exists for, and it was doing exactly that: the sheet can be triggered by any
 * wallet event, which does not wait for a convenient moment.
 */
let pendingSessionPrompt: string|null=null;

/** Shows anything deferred while a card was in play. Called once a card ends. */
function flushSessionPrompt(){
  if(!pendingSessionPrompt)return;
  const msg=pendingSessionPrompt;
  pendingSessionPrompt=null;
  /* After the celebration, not on top of it. */
  setTimeout(()=>{ if(resolved)openSession(msg); },1200);
}

async function warnIfSessionKeyUnusable(acct: string){
  try{
    const { units, deadline }=await sessionAllowance(acct);
    const now=BigInt(Math.floor(Date.now()/1000));
    if(bal>=tier.price&&(units<tier.price||deadline<=now)){
      /* Was "use + ADD to re-enable play", which is both indirect and wrong:
         + ADD deposits, so it renews the session only as a side effect of
         moving more money in. Renewing should not cost the player a deposit. */
      const msg=deadline<=now
        ? "Your last session ran out. Approve a new one to keep playing."
        : "Not enough left in this session to cover a card.";
      /* Mid-card, or mid-scratch: queue it rather than interrupt. */
      if(!resolved||scratching){ pendingSessionPrompt=msg; return; }
      openSession(msg);
    }
  }catch(e){ /* advisory only */ }
}

async function dealOnchain(){
  if(onchainBusy)return;
  const acct=window.__onchain&&window.__onchain.account&&window.__onchain.account();
  if(!acct){
    /* Was: open the DEPOSIT sheet with "Connect a wallet to play" — which
       cannot deposit either, so the player landed one dead end further in. */
    if(window.__onchain&&window.__onchain.connect){ window.__onchain.connect(); return; }
    openDeposit("Connect a wallet to play"); return;
  }

  if(spendLimit!==null&&spentSession+tier.price>spendLimit){
    toast(`Session limit reached (${money(spendLimit)}). Limit changes apply next session.`);
    renderLimit(); return;
  }

  onchainBusy=true;
  const buy=$("buyBtn");
  buy.disabled=true;
  $("revealBtn").disabled=true;
  /* The wait is real — a relayed settlement on a live chain — so the button
     says so. Greyed out with its old label, a second of nothing read as the
     tap not registering, and the complaint was "I can't play again". */
  const label=buy.textContent;
  buy.textContent="Dealing…"; buy.classList.add("busy");
  try{
    /* No wallet round trip here. A play is signed by the session key in this
       tab and posted to the relayer; the wallet is never asked anything, so
       checking which chain it is on was pure cost — instant with an injected
       wallet, and over WalletConnect a relay hop to the wallet app that could
       take seconds, or wait indefinitely for a backgrounded app. Deposits and
       withdrawals DO go through the wallet and keep their check. */
    const res=await chainPlay(acct,tier.id,1);
    const payout=res.payoutUnits;

    /* charge=false: the contract already took the stake. Local bookkeeping
       that still belongs to the UI (session spend, counters) is done here. */
    newCard(false,payout);
    spentSession+=tier.price; renderLimit(); renderPnl();
    played++; $("sPlayed").textContent=played;
    persistState();
    window.__ref&&window.__ref.onPaidPlay(tier.price);
    dealtLocked=false; $("lock").classList.remove("on");

    /* Keep the settled result reachable for the fairness panel and support. */
    window.__lastPlay={txHash:res.txHash,playerSeed:res.playerSeed,
      externalRandomness:res.externalRandomness,randomness:res.randomness,
      rolls:res.cards.map(c=>c.roll)};
    showFairness(res);

    await refreshOnchainBalance();
  }catch(e){
    const msg=(e&&(e as Error).message)||"Could not place the bet";
    if(e instanceof ChainError&&/allowance|session key/i.test(msg)){
      /* Was a bare toast telling the player to "re-authorise" — with nothing
         anywhere in the UI that does it. reauthoriseSession() existed and was
         wired to nothing at all. */
      openSession();
    }else if(/insufficient balance/i.test(msg)){
      openDeposit(`You need ${money(tier.price)} to play ${tier.short}`);
    }else{
      toast(msg);
    }
  }finally{
    onchainBusy=false;
    buy.disabled=false;
    buy.textContent=label; buy.classList.remove("busy");
    $("revealBtn").disabled=false;
  }
}
function dealDemo(){
  newCard(false);
  demoMode=true; dealtLocked=false;
  $("demoBadge").classList.add("on");
  $("lock").classList.remove("on");
}
/* Which lock message is on screen. Needed only so a currency switch can
   repaint the one that is showing: the two variants say different things —
   one prompts a deposit, the other quotes the tier — and picking by
   dealtLocked alone silently turns the first into the second. */
let lockVariant:"tier"|"deposit"|null=null;
function showTierLock(){
  lockVariant="tier";
  $("lockMsg").textContent=`${tier.short} — ${money(tier.price)} a play, top prize ${money(tier.top)}. Odds 1 in ${(1/tier.prizes.reduce((a,[,p])=>a+p,0)).toFixed(1)} to win something.`;
  $("lockBtn").textContent=bal>=tier.price?`Play ${money(tier.price)} card`:`Add funds`;
  $("lockBtn").onclick=()=>{
    audioInit();
    if(bal<tier.price){openDeposit(`You need ${money(tier.price)} to play ${tier.short}`);return;}
    dealtLocked=false; $("lock").classList.remove("on");
    if(isOnchainConfigured())dealOnchain(); else newCard(true);
  };
  $("lock").classList.add("on");
}
function showLock(){
  lockVariant="deposit";
  $("lockMsg").textContent=`Deposit to reveal your ${tier.short} card. ${money(tier.price)} a play, top prize ${money(tier.top)}`;
  $("lock").classList.add("on");
}

/* ======================= CARD GENERATION ======================= */
/* NOT PRODUCTION-SAFE FOR REAL MONEY.
   The outcome is still drawn locally from Math.random(). The vault's
   buyCard/CardResolved path exists (chain.ts) but nothing here calls it, so a
   real-money build would be paying out against a client-side RNG while taking
   deposits on-chain. Wiring this to the vault is tracked as the next task on
   this branch — see MIGRATION.md. Until then the build is testnet/demo only. */
function pickOutcome(){
  if(IS_DEMO_BUILD){
    /* Demo/QA outcome override. Compiled out of the production bundle: the
       flag is a literal `false` there, so this block, the import and the whole
       demo module are dropped by the bundler (verified by `npm run guard`). */
    const forced=takeForced();
    if(forced!==null)return forced;
  }
  const r=Math.random(); let acc=0;
  for(const [amt,p] of tier.prizes){acc+=p;if(r<acc)return amt;}
  return 0;
}
/* `decided` is the payout the CHAIN returned. When present the grid is built to
   display that outcome; nothing local decides it. Absent = local/demo draw. */
function genGrid(decided?: number){
  prize = decided !== undefined ? decided : pickOutcome();
  const counts={}, arr=[];
  const canAdd=s=>(counts[s.id]||0)<2;
  const add=s=>{counts[s.id]=(counts[s.id]||0)+1;arr.push(s);};
  if(prize>0){
    const wi=tier.prizes.findIndex(p=>p[0]===prize);
    const w=ALLSYMS[wi]; arr.push(w,w,w); counts[w.id]=3;
  }
  while(arr.length<9){
    let s=null;
    if(Math.random()<.38){ /* stray moons → the big near-miss hunts */
      const pool=MOONS.filter(canAdd);
      if(pool.length)s=pool[(Math.random()*pool.length)|0];
    }
    if(!s){
      const pool=FILLERS.filter(canAdd);
      if(pool.length)s=pool[(Math.random()*pool.length)|0];
    }
    if(!s){ /* pools exhausted — anything with capacity left */
      const pool=ALLSYMS.filter(canAdd);
      s=pool[(Math.random()*pool.length)|0];
    }
    add(s);
  }
  for(let i=arr.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1));[arr[i],arr[j]]=[arr[j],arr[i]];}
  return arr;
}
/* Display units are the paytable's "dollars". On-chain they denominate the
   settlement asset via SETTLEMENT.unitWei, so amounts render in that asset
   rather than as currency the game does not actually hold. */
function money(v){
  if(isOnchainConfigured())return formatUnits_(v)+" "+SETTLEMENT.display.symbol;
  return "$"+(v<1?v.toFixed(2):(v%1?v.toFixed(2):v.toLocaleString()));
}
function prizeLabel(id){
  const idx=ALLSYMS.findIndex(m=>m.id===id);
  return money(tier.prizes[idx][0]);
}

/* ======================= FOIL ======================= */
const foil=$("foil"), fctx=foil.getContext("2d",{willReadFrequently:true});
let dpr=1, rects=[], revealedCells=new Set<number>(), foilGen=0;
/* session spend limit (responsible gambling). In-memory for the demo; a real
   build persists server-side per Gate 5. Decreases apply instantly; increases
   are deferred to the next session — the standard RG pattern. */
/* A spend cap that resets on refresh is worse than none — it tells the player
   they are protected when they are not. Both halves persist, because the cap
   without the spend against it is just as empty. */
let spendLimit=saved.spendLimit, spentSession=saved.spentSession;
function drawFoil(){
  foilGen++; /* invalidates in-flight giga animations from the previous card */
  const play=$("play"), r=play.getBoundingClientRect();
  dpr=Math.min(window.devicePixelRatio||1,2);
  foil.width=r.width*dpr; foil.height=r.height*dpr;
  fctx.setTransform(dpr,0,0,dpr,0,0);
  fctx.globalCompositeOperation="source-over";
  const g=fctx.createLinearGradient(0,0,r.width,r.height);
  g.addColorStop(0,"#EDEFF4");g.addColorStop(.25,"#B8BEC9");g.addColorStop(.5,"#E8EBF0");
  g.addColorStop(.75,"#A9B0BD");g.addColorStop(1,"#DDE1E8");
  fctx.fillStyle=g; fctx.fillRect(0,0,r.width,r.height);
  for(let i=0;i<420;i++){
    fctx.fillStyle=Math.random()<.5?"rgba(255,255,255,.7)":"rgba(90,98,115,.35)";
    const s=Math.random()*1.8+.4;
    fctx.fillRect(Math.random()*r.width,Math.random()*r.height,s,s);
  }
  fctx.save();
  fctx.translate(r.width/2,r.height/2); fctx.rotate(-0.18);
  fctx.fillStyle="rgba(90,98,115,.55)";
  fctx.font="900 17px Nunito, sans-serif"; fctx.textAlign="center";
  for(let y=-r.height;y<r.height;y+=44){fctx.fillText("SCRATCH • TO • REVEAL • ✦ •",0,y);}
  fctx.restore();
  const pr=play.getBoundingClientRect();
  rects=[...document.querySelectorAll(".cell")].map(c=>{
    const b=c.getBoundingClientRect();
    return {x:b.left-pr.left,y:b.top-pr.top,w:b.width,h:b.height};
  });
  revealedCells.clear();
  foil.classList.remove("done");
}

/* ======================= GAME FLOW ======================= */
/* A bought card's outcome is the player's property the moment it's dealt —
   on-chain it settles at the VRF callback regardless of what the UI does.
   Abandoning a card must NEVER burn its prize. */
function settleAbandoned(){
  if(resolved||demoMode||!cells.length)return;
  /* Never settle a card nobody paid for. This is what produced money from
     nothing: the display card dealt at page load is not demoMode, so the guard
     above let it through, and abandoning it for the onboarding free card
     credited its prize into the balance. */
  if(!cardStaked)return;
  resolved=true;
  if(prize>0){
    /* On-chain the payout landed in the player's contract balance in the same
       transaction that resolved the card — there is nothing left to settle,
       and adding it here would show money the contract does not hold. The
       local branch below is for the demo/local RNG path only. */
    if(!onchainSettled)bal+=prize;
    totalWon+=prize; best=Math.max(best,prize);
    logHistory(prize, prize>tier.price*25, prize===tier.price, prize>0&&prize<tier.price);
    toast(`Previous card settled: +${money(prize)}`);
    if(onchainSettled)refreshOnchainBalance(); else renderBal(true);
  }else{
    logHistory(0,false,false,false);
  }
}
function toast(msg){
  try{
    const t=document.createElement("div");
    t.className="toast"; t.textContent=msg;
    document.body.appendChild(t);
    setTimeout(()=>t.classList.add("on"),20);
    setTimeout(()=>{t.classList.remove("on");setTimeout(()=>t.remove(),400);},2600);
  }catch(e){}
}
function newCard(charge=true, decided?: number){
  /* ORDER MATTERS: settleAbandoned() settles the PREVIOUS card and reads
     `onchainSettled` to decide whether that card's prize was already credited
     on-chain. It must run before the flag is reassigned for this card, below.
     Moving the reassignment above this call silently double-credits every
     abandoned on-chain win. */
  settleAbandoned();
  if(charge){
    if(spendLimit!==null&&spentSession+tier.price>spendLimit){
      toast(`Session limit reached (${money(spendLimit)}). Limit changes apply next session.`);
      renderLimit(); return;
    }
    if(bal<tier.price){openDeposit(`You need ${money(tier.price)} to play ${tier.short}`);return;}
    bal-=tier.price; renderBal();
    spentSession+=tier.price; renderLimit(); renderPnl();
    played++; $("sPlayed").textContent=played;
    persistState();
    window.__ref&&window.__ref.onPaidPlay(tier.price);
  }
  stopTension(); clearShavings();
  demoMode=false; $("demoBadge").classList.remove("on");
  document.body.classList.remove("vegasEdges","celebrate","critical");
  $("winsign").classList.remove("on");
  $("buyBtn").classList.remove("pulse");
  $("shine").classList.remove("off");
  $("beam2").classList.remove("on");
  gigaOn=false; foil.style.pointerEvents=""; $("revealBtn").disabled=false;
  $("serialNo").textContent="NO. "+String(tier.id+1).padStart(3,"0")+"-"+String(1e6+((Math.random()*9e6)|0));
  armFairness();
  whoosh();
  if(!dealtLocked)$("lock").classList.remove("on");
  const t=$("ticket");
  t.className="ticket "+tier.tCls;
  t.style.animation="none"; void t.offsetWidth; t.style.animation="";
  $("tName").innerHTML=tier.name;
  $("tTop").textContent=money(tier.top);
  $("banner").className="banner";
  /* True only when the chain decided this card. Derived per card rather than
     set by hand, so it can never linger from a previous on-chain play into a
     demo card and suppress a credit that should happen (or vice versa). */
  onchainSettled = decided !== undefined;
  /* Staked means the player is owed this card's outcome: they either paid for
     it locally, or it came back already settled from the chain. Display cards
     (charge=false, no payout) are neither. */
  cardStaked = charge || decided !== undefined;
  cells=genGrid(decided); resolved=false; winSet=null;
  if(prize>0){
    const k=Object.entries<number[]>(cells.reduce((m,c,i)=>((m[c.id]=m[c.id]||[]).push(i),m),{})).find(([,v])=>v.length===3);
    winSet=new Set(k[1]);
  }
  $("grid").innerHTML=cells.map((c,i)=>`
    <div class="cell ${c.type==="moon"?"m":"j"}" data-i="${i}" style="background:${c.cell};${c.type==="moon"?`--glow:${c.hue[1]}`:""}">
      <div class="sym-moon" style="${c.type==="moon"?"":"width:58%"}">${symArt(c)}</div>
      <div class="amt">${prizeLabel(c.id)}</div>
    </div>`).join("");
  requestAnimationFrame(drawFoil);
}

let last=null, flakeAcc=0, gritAcc=0, lastT=0;
let foilRect=null;
function pt(e){const b=foilRect||foil.getBoundingClientRect();const t=e.touches?e.touches[0]:e;return{x:t.clientX-b.left,y:t.clientY-b.top};}
function scratch(a,b){
  fctx.globalCompositeOperation="destination-out";
  fctx.lineWidth=44; fctx.lineCap="round"; fctx.lineJoin="round";
  fctx.beginPath(); fctx.moveTo(a.x,a.y); fctx.lineTo(b.x,b.y); fctx.stroke();
  const dist=Math.hypot(b.x-a.x,b.y-a.y);
  const now=performance.now(), dt=Math.max(8,now-lastT); lastT=now;
  scratchSound(dist/(dt/16.7));
  gritAcc+=dist; flakeAcc+=dist;
  if(gritAcc>6){gritAcc=0;grit();}
  /* Nothing left to shave once the card has resolved. finish() sweeps the
     pile away, but the hand is usually still moving when it fires — and every
     move after that emitted fresh flakes which then settled and sat at the
     foot of the card for their full 600-frame life. Measured: a band of
     debris under a finished card for ten seconds, long after the sweep. */
  if(!resolved&&flakeAcc>13){
    flakeAcc=0;
    const fb=foilRect||foil.getBoundingClientRect();
    emitShavings(fb.left+b.x,fb.top+b.y,2+((Math.random()*2)|0));
  }
}
/* iOS Safari did not support overscroll-behavior until 16, so on older iPhones
   the CSS alone does not stop the rubber-band while scratching. A non-passive
   touchmove that preventDefaults covers every version, and is harmless where
   touch-action already did the job. Registered on the canvas only, so the rest
   of the page still scrolls normally. */
foil.addEventListener("touchmove",e=>{
  if(scratching)e.preventDefault();
},{passive:false});

foil.addEventListener("pointerdown",e=>{
  audioInit();
  resumeAudio();
  if(resolved&&foil.classList.contains("done"))return;
  foilRect=foil.getBoundingClientRect();
  scratching=true;last=pt(e);lastT=performance.now();
  scratch(last,{x:last.x+.1,y:last.y+.1});
  foil.setPointerCapture(e.pointerId);
});
let lastLiveCheck=0;
foil.addEventListener("pointermove",e=>{
  if(!scratching)return;
  /* mouse released outside / capture lost: hover-move arrives with no buttons down */
  if(e.pointerType==="mouse"&&e.buttons===0){endScratch();return;}
  const p=pt(e);scratch(last,p);last=p;
  /* PERF/FEEL v2: cells pop live under the finger (throttled), not on release */
  const now=performance.now();
  if(now-lastLiveCheck>90){lastLiveCheck=now;checkCells();}
});
function endScratch(){
  if(!scratching)return;
  scratching=false; foilRect=null; scratchStop(); checkCells();
  /* Backstop for anything still in the air. finish() sweeps at the moment the
     card resolves, which is usually mid-drag; this catches flakes emitted in
     the frames before that and any left mid-flight when the hand lifts. */
  if(resolved)sweepShavings();
}
["pointerup","pointercancel"].forEach(ev=>foil.addEventListener(ev,endScratch));
["pointerup","pointercancel"].forEach(ev=>window.addEventListener(ev,endScratch));
window.addEventListener("blur",()=>{scratching=false;scratchStop();});

function checkCells(only?){
  /* PERF v2 (+scope): getImageData is a GPU→CPU sync stall, so do ONE readback over
     the bounding box of still-covered cells instead of one per cell. Runs
     live during the gesture now, so the readback count matters. Cost still
     falls as cells resolve and hits zero once the card is open. */
  let newly=[];
  const step=Math.max(4,Math.round(8*dpr));
  let bx0=Infinity,by0=Infinity,bx1=-1,by1=-1,anyPending=false;
  for(let i=0;i<rects.length;i++){
    if(revealedCells.has(i))continue;
    if(only&&only.indexOf(i)<0)continue;
    anyPending=true;
    const r=rects[i];
    if(r.x<bx0)bx0=r.x; if(r.y<by0)by0=r.y;
    if(r.x+r.w>bx1)bx1=r.x+r.w; if(r.y+r.h>by1)by1=r.y+r.h;
  }
  if(anyPending){
    const sx=Math.max(0,(bx0*dpr)|0), sy=Math.max(0,(by0*dpr)|0);
    const sw=Math.min(foil.width-sx,((bx1-bx0)*dpr)|0), sh=Math.min(foil.height-sy,((by1-by0)*dpr)|0);
    if(sw>0&&sh>0){
      const img=fctx.getImageData(sx,sy,sw,sh).data;
      for(let i=0;i<rects.length;i++){
        if(revealedCells.has(i))continue;
        if(only&&only.indexOf(i)<0)continue;
        const r=rects[i];
        /* sample the CENTRAL symbol zone, not the full rect: players "see" a
           symbol long before 60% of the whole cell is scraped. Perception is
           the ground truth for reveal, otherwise pairs sit invisible to the
           game while staring the player in the face. */
        const inX=r.w*.22, inY=r.h*.22;
        const cx=((r.x+inX)*dpr)|0, cy=((r.y+inY)*dpr)|0;
        const cw=((r.w-2*inX)*dpr)|0, ch=((r.h-2*inY)*dpr)|0;
        let clear=0,total=0;
        for(let y=0;y<ch;y+=step)
          for(let x=0;x<cw;x+=step){
            const gx=(cx-sx+x)|0, gy=(cy-sy+y)|0;
            if(gx<0||gy<0||gx>=sw||gy>=sh)continue;
            total++;
            if(img[((gy*sw)+gx)*4+3]<40)clear++;
          }
        if(total&&clear/total>.5){revealedCells.add(i);newly.push(i);}
      }
    }
  }
  const all=revealedCells.size===rects.length;
  newly.forEach((i,n)=>{
    const el=document.querySelector<HTMLElement>(`.cell[data-i="${i}"]`);
    el.classList.remove("revealed"); void el.offsetWidth; el.classList.add("revealed");
    setTimeout(tickSnd,n*70);
  });
  if(newly.length)updateTension();
  /* Resolve the instant the WINNING set is fully uncovered — winSet is the
     single source of truth (fixed at deal, matches the prize). We do not
     scan for "any three alike": a losing card must never self-resolve as a
     win, and the paytable guarantees no stray non-winning triple exists. */
  if(!resolved && winSet){
    let done=true;
    winSet.forEach(ix=>{if(!revealedCells.has(ix))done=false;});
    if(done){ setTimeout(()=>{if(!resolved)finish(true);},350); return; }
  }
  if(all&&!resolved)finish(false);
}

let hotId=null;
function updateTension(){
  try{ updateTensionInner(); }
  catch(e){
    console.error("tension error:",e);
    try{$("tbar").textContent="⚠ tension error: "+e.message;$("tbar").classList.add("on");}catch(_){}
  }
}
function updateTensionInner(){
  if(resolved)return;
  const seen: Record<string, number[]> = {};
  revealedCells.forEach(i=>{const c=cells[i];(seen[c.id]=seen[c.id]||[]).push(i);});
  /* newest pair wins the spotlight, so a fresh match always announces itself */
  const pairs=Object.entries<number[]>(seen).filter(([,v])=>v.length===2);
  const hot=pairs.sort((a,b)=>Math.max(...b[1])-Math.max(...a[1]))[0];
  document.querySelectorAll<HTMLElement>(".cell.hot").forEach(c=>c.classList.remove("hot"));
  if(hot && revealedCells.size<9){
    hot[1].forEach(i=>document.querySelector<HTMLElement>(`.cell[data-i="${i}"]`).classList.add("hot"));
    const lastCell=revealedCells.size===8;
    document.body.classList.toggle("critical",lastCell);
    const left=9-revealedCells.size;
    $("tbar").innerHTML=`<span class="pips">●●<span class="pipOff">○</span></span> 2 OF 3 ${NAMES[hot[0]]}`
      +(lastCell?` — <b>LAST CELL!</b>`:` — one more, ${left} cell${left>1?"s":""} left`);
    $("tbar").classList.add("on");
    $("grid").classList.add("dimmed");
    positionBeam(hot[1][0],hot[1][1]);
    /* pulse quickens as the card fills: 900ms → 420ms on the last cell */
    const rate=Math.max(420, 980 - revealedCells.size*70);
    const fresh=hot[0]!==hotId;
    if(!tension||fresh){
      tension=true; hotId=hot[0];
      document.body.classList.add("tension");
      stinger(); shake();
      navigator.vibrate&&navigator.vibrate([40,30,40]);
    }
    tensionStart(rate);
  }else stopTension();
}
function positionBeamEl(bm,a,b){
  const pr=$("play").getBoundingClientRect();
  const ca=cellCenter(a), cb=cellCenter(b);
  const x1=ca.x-pr.left, y1=ca.y-pr.top, x2=cb.x-pr.left, y2=cb.y-pr.top;
  const len=Math.hypot(x2-x1,y2-y1), ang=Math.atan2(y2-y1,x2-x1)*180/Math.PI;
  bm.style.left=x1+"px"; bm.style.top=(y1-3.5)+"px";
  bm.style.width=len+"px";
  bm.style.transform=`rotate(${ang}deg)`;
  bm.classList.add("on");
}
function positionBeam(a,b){positionBeamEl($("beam"),a,b);}
function drawWinLine(){
  const idx=[...winSet].sort((a,b)=>a-b);
  positionBeamEl($("beam"),idx[0],idx[1]);
  positionBeamEl($("beam2"),idx[1],idx[2]);
}
function stopTension(){
  tension=false; hotId=null;
  document.body.classList.remove("tension","critical");
  $("tbar").classList.remove("on");
  $("grid").classList.remove("dimmed");
  $("beam").classList.remove("on");
  document.querySelectorAll<HTMLElement>(".cell.hot").forEach(c=>c.classList.remove("hot"));
  tensionStop();
}

/* `label` is prepended, `to` is a display-unit amount rendered through money()
   so the count-up lands in the settlement denomination rather than dollars. */
function rollNumber(el,to,ms,label="",onTick){
  const t0=performance.now(); let lastTick=0;
  (function step(t){
    const p=Math.min(1,(t-t0)/ms), e=1-Math.pow(1-p,3);
    el.textContent=label+money(to*e);
    if(onTick&&t-lastTick>48&&p<1){lastTick=t;onTick(p);}
    if(p<1)requestAnimationFrame(step);
  })(t0);
}

function finish(forced){
  resolved=true; stopTension(); scratchStop();
  if(forced)foil.classList.add("done"); else setTimeout(()=>foil.classList.add("done"),150);
  $("shine").classList.add("off");
  sweepShavings(); whoosh();
  /* A renewal prompt deferred while this card was in play can surface now. */
  flushSessionPrompt();
  /* Only real cards. Counting demo and display cards here is what produced
     "2 cards scratched" beside "0 cards played" — two counters measuring
     different things under labels that read as the same thing. */
  if(!demoMode&&cardStaked)bumpTotal();
  setTimeout(()=>$("buyBtn").classList.add("pulse"), prize>0?2600:900);
  const b=$("banner");
  if(prize>0){
    const isDemo=demoMode;
    /* Three honest outcomes (per spec): profit, stake covered, partial return.
       Only a profit is a win. A partial return is money back on a net loss
       and is presented as exactly that — never celebrated. The rule itself now
       lives in outcome.ts so it can be unit-tested; this must stay declared
       BEFORE any use below — it was previously read one line early, which threw
       a TDZ ReferenceError and killed every payout render. */
    const oc=classifyOutcome(prize,tier.price);
    const mult=oc.mult, net=oc.net;
    const evenOnly=oc.kind==="covered", subStake=oc.kind==="partial", quiet=oc.quiet;
    if(!isDemo){
      /* On-chain settlement already moved the money; refreshOnchainBalance()
         brings `bal` back from the contract. Only the local/demo path adds. */
      if(!onchainSettled)bal+=prize;
      totalWon+=prize; best=Math.max(best,prize); if(!quiet)streak++;
    }
    const isJackpot=!isDemo&&!quiet&&prize>=tier.top, isBig=!isDemo&&!quiet&&prize>=tier.price*25;
    /* How long the celebration owns the screen. Declared here rather than
       inside the animation closure so the free-card upsell can wait for it
       instead of opening on a flat timer over the top of the win. */
    const hold=isJackpot?6000:isBig?4200:2600;
    const rung=tier.prizes.findIndex(x=>x[0]===prize);
    const rar=rung>=0?Math.round(1/tier.prizes[rung][1]):0;
    const winSym=winSet?NAMES[cells[[...winSet][0]].id]:"";
    $("bSub").textContent=isDemo?"Free card win, same odds as paid. Deposit to keep what you win"
      :evenOnly?`Match paid your ${money(tier.price)} back · card covered · net $0`
      :subStake?`Partial return — less than the ${money(tier.price)} card price · net −${money(tier.price-prize).slice(1)}`
      :`+${money(net)} net · ${mult.toLocaleString()}× · 1 in ${rar.toLocaleString()}`
        +(isBig?" · Very tidy.":"")+(streak>1?` · 🔥 ${streak} straight`:"");
    if(!isDemo&&mult>1){
      window.__lastWin={prize,mult,price:tier.price};
      const sw=document.getElementById("shareWin");
      if(sw){sw.style.display="";sw.textContent=`Share your ${mult.toLocaleString()}× win`;}
    }
    $("bBig").textContent=evenOnly?`3× ${winSym} · CARD COVERED`
      :subStake?`3× ${winSym} · ${money(prize)} BACK`:"YOU WON "+money(0);

    /* PHASE 1 — anticipation: everything holds its breath */
    riser();

    /* PHASE 2 (t+420ms) — DETONATION from the winning cells */
    setTimeout(()=>{
      winSet.forEach(i=>document.querySelector<HTMLElement>(`.cell[data-i="${i}"]`).classList.add("winner"));
      drawWinLine();
      if(!quiet)[...winSet].forEach((i,n)=>setTimeout(()=>{
        explodeFromCell(i);
        floaterFromCell(i,"+$"+prize.toLocaleString());
      },n*90));
      if(quiet){
        /* money back, no win: acknowledge, celebrate nothing */
        if(evenOnly){tone(660,.14,"sine",.18,0,.2); tone(880,.16,"sine",.12,.09,.2);}
        else{tone(520,.16,"sine",.14,0,.2);}
        navigator.vibrate&&navigator.vibrate(30);
      }else{
        $("winsignText").textContent=isJackpot?"★ JACKPOT ★":"★ WINNER ★";
        $("winsign").classList.remove("on"); void $("winsign").offsetWidth; $("winsign").classList.add("on");
        $("ticket").classList.add("vegas");
        document.body.classList.add("vegasEdges");
        /* The grid, not .ticket-inner: the symbols pop without dragging the
           controls below them around. */
        const inner=$("grid") as HTMLElement;
        inner.classList.remove("punch"); void inner.offsetWidth; inner.classList.add("punch");
        $("flash").classList.remove("go"); void $("flash").offsetWidth; $("flash").classList.add("go");
        dingDingDing();
        isJackpot||isBig?megaShake():shake();
        navigator.vibrate&&navigator.vibrate(isJackpot?[90,40,90,40,240]:isBig?[70,35,140]:[50,30,110]);
      }
    },420);

    /* PHASE 3 (t+700ms) — the payout wave: rolling counter, clinks, cascades */
    setTimeout(()=>{
      if(quiet){
        b.className=(evenOnly?"banner even show":"banner part show");
        renderBal(true);
      }else{
      b.className="banner win show";
      document.body.classList.add("celebrate");
      rollNumber($("bBig"),prize,isJackpot?1800:isBig?1300:900,"YOU WON ",payClink);
      renderBal(true);
      winSet.forEach(i=>{const c=cellCenter(i);confettiFrom(c.x,c.y,isBig||isJackpot?90:55);});
      if(isJackpot){
        siren(); megafanfare();
        $("rays").classList.add("on");
        $("jAmt").textContent=money(0);
        $("jackpot").classList.add("on");
        rollNumber($("jAmt"),prize,2100,"",payClink);
        confetti(340); coinRain(3600);
        setTimeout(()=>{confetti(220);megaShake();},1300);
      }else if(isBig){
        fanfare(); coinClink(12);
        $("rays").classList.add("on"); setTimeout(()=>$("rays").classList.remove("on"),3000);
        confetti(260); coinRain(1800);
        setTimeout(()=>confetti(160),1100);
      }else{
        fanfare(); coinClink(); confetti(160);
      }
      }
      setTimeout(()=>{document.body.classList.remove("vegasEdges","celebrate")},hold);
    },700);

    setTimeout(()=>{$("winsign").classList.remove("on")},3400);
    if(isDemo&&onboardingDemo){
      onboardingDemo=false;
      dealtLocked=true;
      /* The celebration runs for `hold` ms (up to 6s on a jackpot) and this
         used to open at a flat 3.2s, covering the win it was congratulating.
         Wait for the celebration to finish, then a beat. */
      setTimeout(()=>{
        showLock();
        openDeposit(`You just won ${money(prize)} on a free card 🎉 Deposit to keep what you win`);
      },hold+900);
    }
  }else{
    streak=0; womp();
    $("ticket").classList.remove("vegas");
    document.body.classList.remove("vegasEdges","celebrate");
    $("bBig").textContent="Not this time";
    $("bSub").textContent="Run it back or take a breather";
    b.className="banner lose show";
    if(demoMode&&onboardingDemo){
      onboardingDemo=false; dealtLocked=true;
      setTimeout(()=>{
        showLock();
        openDeposit("Free card played, same odds as the real thing. Deposit to play for keeps");
      },1800);
    }
  }
  $("streak").textContent=`🔥 streak ×${streak}`;
  $("streak").classList.toggle("on",streak>1);
  $("sWon").textContent=money(totalWon);
  renderPnl();
  $("sBest").textContent=money(best);
  /* Demo cards stay out of the strip, for the same reason they stay out of the
     ticker: LAST 12 is described as the player's real record, and mixing free
     play into it is what made the counters disagree. */
  if(!demoMode)logHistory(prize, prize>tier.price*25, prize===tier.price, prize>0&&prize<tier.price);
}
/* session history: the player's real record, visible. transparency and
   engagement in one — an RG-positive feature, not a manufactured one. */
const histLog=saved.history.slice();
/* Single writer, so no caller has to remember which fields matter. */
function persistState(){
  persist.save({
    spendLimit, spentSession, played, totalWon, best, streak,
    history: histLog, scratched: totalN,
    freeCardUsed: saved.freeCardUsed, welcomeSeen: saved.welcomeSeen
  });
}

function logHistory(prize,big,even,part){
  histLog.push({prize,big,even,part});
  if(histLog.length>12)histLog.shift();
  const h=$("history");
  h.innerHTML='<span class="hlabel">LAST 12</span>'+histLog.map(x=>
    `<i class="${x.prize>0?(x.even?"e":(x.part?"p":(x.big?"w big":"w"))):""}" title="${x.prize>0?(x.even?"stake back":(x.part?"partial return":money(x.prize))):"no win"}"></i>`
  ).join("");
  persistState();
}

function cellCenter(i){
  const b=document.querySelector<HTMLElement>(`.cell[data-i="${i}"]`).getBoundingClientRect();
  return {x:b.left+b.width/2, y:b.top+b.height/2};
}
function explodeFromCell(i){
  const c=cellCenter(i), hue=cells[i].hue||["#7AFFB0","#00D632"];
  const palette=[hue[0],hue[1],"#7AFFB0","#FFFFFF","#00D632"];
  const K=Math.min(70,MAX_PARTS-parts.length);
  for(let k=0;k<K;k++){
    const a=Math.random()*Math.PI*2, sp=3+Math.random()*11;
    parts.push({t:"s",x:c.x,y:c.y,vx:Math.cos(a)*sp,vy:Math.sin(a)*sp-1.5,
      g:.16,life:34+Math.random()*26,c:palette[(Math.random()*palette.length)|0],
      len:5+Math.random()*9});
  }
  parts.push({t:"w",x:c.x,y:c.y,r:10,vr:11,life:26});
  parts.push({t:"w",x:c.x,y:c.y,r:4,vr:7,life:32});
  ensureTick();
}
function floaterFromCell(i,text){
  const c=cellCenter(i);
  const d=document.createElement("div");
  d.className="floater"; d.textContent=text;
  d.style.left=c.x+"px"; d.style.top=(c.y-20)+"px";
  document.body.appendChild(d);
  setTimeout(()=>d.remove(),1600);
}
function confettiFrom(x,y,n){
  n=Math.min(n,MAX_PARTS-parts.length); if(n<=0)return;
  for(let i=0;i<n;i++){
    const a=Math.random()*Math.PI*2, sp=4+Math.random()*10;
    parts.push({t:"c",x,y,vx:Math.cos(a)*sp,vy:Math.sin(a)*sp-6,
      g:.32,rot:Math.random()*Math.PI,vr:(Math.random()-.5)*.35,
      s:Math.random()*7+4,c:CCOLORS[i%CCOLORS.length],life:100+Math.random()*50,coin:Math.random()<.3});
  }
  ensureTick();
}

$("jClose").onclick=()=>{$("jackpot").classList.remove("on");$("rays").classList.remove("on");document.body.classList.remove("vegasEdges","celebrate");};
$("revealBtn").onclick=()=>gigaScratch();
/* GIGA SCRATCH v3 — the paint stripper. A steel blade strips the foil
   line by line with a ragged chip edge, shavings flying off the tip and
   an industrial scrape underneath. Pure reveal theatre: the outcome is
   fixed at purchase and no reveal mechanic can change it. Autoplay
   remains a per-market feature flag (prohibited in GB slots rules). */
let gigaOn=false;
function clank(){
  tone(196,.09,"square",.22,0,.25); tone(311,.07,"square",.14,.02,.25);
  noiseBurst(.03,"highpass",2400,.16,0,.2,1.2);
}
function thunk(){ tone(82,.16,"sine",.4,0,0,58,.004); noiseBurst(.04,"lowpass",600,.22,0,0,.6); }
function chip(){ noiseBurst(.02+Math.random()*.02,"bandpass",700+Math.random()*900,.15+Math.random()*.1,0,0,.5+Math.random()*.6); }
function gigaScratch(){
  if(gigaOn||resolved||dealtLocked)return;
  audioInit(); resumeAudio();
  if(window.matchMedia&&matchMedia("(prefers-reduced-motion: reduce)").matches){finish(true);return;}
  gigaOn=true; foil.style.pointerEvents="none"; $("revealBtn").disabled=true;
  const gen=foilGen, W=foil.width/dpr, H=foil.height/dpr;
  const blade=$("blade"), app=$("play");
  blade.classList.add("on"); app.classList.add("rumble");
  fctx.fillStyle="#000";
  const cleanup=()=>{gigaOn=false;foil.style.pointerEvents="";$("revealBtn").disabled=false;
    blade.classList.remove("on","lift");app.classList.remove("rumble");scrapeOff();};
  /* three horizontal bands from the three cell rows */
  const bands=[0,1,2].map(r=>{
    const rs=rects.slice(r*3,r*3+3);
    const y0=Math.min(...rs.map(c=>c.y))-6, y1=Math.max(...rs.map(c=>c.y+c.h))+6;
    return {y0:Math.max(0,y0), y1:Math.min(H,y1)};
  });
  let lastCheck=0, acc=0;
  function stripTo(prevX,x,b,dir){
    fctx.globalCompositeOperation="destination-out";
    const x0=Math.min(prevX,x), x1=Math.max(prevX,x);
    fctx.fillRect(x0-2,b.y0,(x1-x0)+4,b.y1-b.y0);
    for(let k=0;k<4;k++){ /* ragged chipping edge, never a clean line */
      const ex=x+dir*(Math.random()*12), ey=b.y0+Math.random()*(b.y1-b.y0);
      fctx.beginPath(); fctx.arc(ex,ey,5+Math.random()*10,0,7); fctx.fill();
    }
  }
  function bladeAt(x,b,dir){
    /* transform-only: left/top/height per frame force layout; translate3d composites */
    blade.style.transform=`translate3d(${x}px,${(b.y0+b.y1)/2}px,0) translate(-50%,-50%) rotate(${dir>0?7:-7}deg) translate(${(Math.random()-.5)*2}px,${(Math.random()-.5)*3}px)`;
  }
  function bladeSize(b){blade.style.height=(b.y1-b.y0+10)+"px";}
  function runBand(bi){
    if(gen!==foilGen||resolved){cleanup();return;}
    if(bi>=bands.length){finishUp();return;}
    const b=bands[bi], dir=bi%2===0?1:-1, bandCells=[bi*3,bi*3+1,bi*3+2];
    const dur=[950,780,620][bi]; /* each pass hungrier than the last */
    const startX=dir>0?-30:W+30, endX=dir>0?W+30:-30;
    bladeSize(b);
    const fb=foil.getBoundingClientRect(); /* cached per band, not per emission */
    thunk(); scrapeOn();
    const t0=performance.now(); let prevX=startX;
    (function frame(t){
      if(gen!==foilGen||resolved){cleanup();return;}
      const p=Math.min(1,(t-t0)/dur), x=startX+(endX-startX)*p;
      stripTo(prevX,x,b,dir); bladeAt(x,b,dir);
      acc+=Math.abs(x-prevX);
      if(acc>14){acc=0; chip();
        emitShavings(fb.left+x, fb.top+(b.y0+b.y1)/2, 2+((Math.random()*3)|0));
      }
      prevX=x;
      if(t-lastCheck>110){lastCheck=t;checkCells(bandCells);}
      if(p<1)requestAnimationFrame(frame);
      else{
        scrapeOff(); checkCells();
        const remaining=rects.map((r,ix)=>ix).filter(ix=>!revealedCells.has(ix));
        if(remaining.length===1&&tension&&bi===bands.length-1){hold(remaining[0]);return;}
        blade.classList.add("lift"); clank();
        setTimeout(()=>{if(gen!==foilGen){cleanup();return;}blade.classList.remove("lift");runBand(bi+1);},220);
      }
    })(t0);
  }
  /* the hesitation: one cell left, live pair — the blade hovers, then commits */
  function hold(cellIdx){
    document.body.classList.add("critical");
    riser();
    const r=rects[cellIdx];
    bladeSize({y0:r.y,y1:r.y+r.h});
    bladeAt(r.x+r.w/2,{y0:r.y,y1:r.y+r.h},1);
    blade.classList.add("lift");
    setTimeout(()=>{
      if(gen!==foilGen){cleanup();return;}
      blade.classList.remove("lift"); thunk(); scrapeOn();
      const t0=performance.now(), dur=900, x0=r.x-24, x1=r.x+r.w+24;
      let prev=x0;
      (function slow(t){
        if(gen!==foilGen){cleanup();return;}
        const p=Math.min(1,(t-t0)/dur), x=x0+(x1-x0)*(p*p*(3-2*p));
        stripTo(prev,x,{y0:r.y-6,y1:r.y+r.h+6},1); bladeAt(x,{y0:r.y,y1:r.y+r.h},1);
        if(Math.random()<.4)chip(); prev=x;
        if(p<1)requestAnimationFrame(slow);
        else{scrapeOff();finishUp();}
      })(t0);
    },1500);
  }
  function finishUp(){
    try{
      fctx.globalCompositeOperation="destination-out";
      fctx.fillRect(0,0,W,H);
      checkCells();
      if(!resolved&&gen===foilGen)finish(true);
    }finally{cleanup();}
  }
  runBand(0);
}
$("buyBtn").onclick=()=>{audioInit();demoSession?dealDemo():(dealtLocked?openDeposit():attemptBuy());};
$("demoToggle").onclick=()=>{
  audioInit();
  demoSession=!demoSession;
  $("demoToggle").classList.toggle("on",demoSession);
  /* The asset bar dims in demo, so it has to be repainted when the mode
     changes rather than only when the asset does. */
  renderCurrencyPicker();
  if(demoSession){dealDemo();}
  else if(bal>=tier.price){
    /* Same trap as unlockIfNeeded had: newCard(true) is the local-RNG path.
       On-chain, leaving demo must go through the contract. */
    if(isOnchainConfigured())dealOnchain(); else newCard(true);
  }else{
    /* Was openDeposit(...) — leaving demo mode fired the deposit sheet
       unprompted, the second of two auto-opens QA flagged. The lock already
       says what is needed and has its own button; let the player ask. */
    dealtLocked=true;newCard(false);showLock();
  }
  renderBal();
};

/* ================= DEPOSIT FLOW ================= */
function openDeposit(msg?){
  renderCurrencyPicker();
  /* "Get test funds" calls the legacy TestUSDC faucet, whose address is blank
     on this deployment — it could only ever produce an error. */
  try{ const f=$("depFaucet"); if(f)f.style.display=isOnchainConfigured()?"none":""; }catch(e){}
  $("depState").textContent=msg||"";
  $("depositModal").classList.add("on");
}
function closeDeposit(){$("depositModal").classList.remove("on");}
$("depOpen").onclick=()=>{audioInit();openDeposit();};
$("lockBtn").onclick=()=>{audioInit();openDeposit();};
$("depClose").onclick=closeDeposit;
$("depFaucet").onclick=()=>{audioInit();window.__onchain.faucet();};
$("depositModal").addEventListener("click",e=>{if(e.target.id==="depositModal")closeDeposit();});

/**
 * Settlement-asset picker.
 *
 * Switching asset is not a display change: balances, session-key allowances
 * and the bankroll are all per-currency on-chain, so the balance has to be
 * re-read and the player will need a deposit (which authorises the session
 * key) before they can play in an asset they have not funded.
 */
function paintAssetChips(row:HTMLElement|null){
  if(!row)return;
  row.innerHTML="";
  CURRENCIES.forEach(c=>{
    const b=document.createElement("button");
    b.className="chip"+(c.id===SETTLEMENT.id?" sel":"");
    b.dataset.cur=c.id;
    b.textContent=c.label;
    if(c.warning)b.title=c.warning;
    b.setAttribute("aria-pressed",String(c.id===SETTLEMENT.id));
    row.appendChild(b);
  });
}

function paintAssetWarning(el:HTMLElement|null){
  if(!el)return;
  el.textContent=SETTLEMENT.warning||"";
  el.className=SETTLEMENT.warning?"sub3 warn":"sub3";
}

function renderCurrencyPicker(){
  const bar=$("assetBar");
  /* The demo build settles nothing, so an asset chooser there would be
     decoration that implies a choice the player does not have. */
  if(!isOnchainConfigured()){
    if(bar)bar.classList.remove("on");
    return;
  }
  /* Two rows, one registry: the bar above the tier prices and the copy inside
     the deposit sheet. Painting both from CURRENCIES is what keeps a new asset
     from needing markup in two places — and from appearing in one of them. */
  paintAssetChips($("assetRow"));
  paintAssetChips($("tokRow"));
  paintAssetWarning($("assetWarn"));
  paintAssetWarning($("curWarn"));
  if(bar){
    bar.classList.add("on");
    /* Demo cards do not settle, so the asset named here would not be the one
       anything happened in. Dimmed rather than hidden, so leaving demo does
       not shift the page. */
    bar.classList.toggle("idle",demoSession);
  }
}

/**
 * Re-label the tier cards for the active asset.
 *
 * The rack is built once at module load rather than by a render function, so
 * rather than restructure it — and risk the tier click wiring that a QA pass
 * has already been through — only the two money labels per card are repainted.
 */
function repaintRackAmounts(){
  document.querySelectorAll<HTMLElement>(".mini").forEach((el,i)=>{
    const t=TIERS[i];
    if(!t)return;
    const top=el.querySelector<HTMLElement>(".top b");
    const price=el.querySelector<HTMLElement>(".price");
    if(top)top.textContent=money(t.top);
    if(price)price.textContent=money(t.price);
  });
  /* The ticket carries the same figure and is painted by newCard(), which a
     currency switch does not call — so it kept the old denomination while the
     rack above it showed the new one: "Top prize 500,000 gwei" on a card the
     chips said was CASHCAT. money() is always right when it runs; the bug is
     text that already ran. */
  const tTop=$("tTop"); if(tTop)tTop.textContent=money(tier.top);
  /* Same trap on the lock overlay, which quotes a price and a top prize and
     stays up across a switch. Only repainted while it is actually showing, so
     this cannot resurrect a message for a card that is not locked. */
  if($("lock")?.classList.contains("on")&&lockVariant){
    lockVariant==="tier"?showTierLock():showLock();
  }
}

async function switchCurrency(id:string){
  if(id===SETTLEMENT.id)return;
  if(!setSettlement(id))return;
  /* Everything denominated changes at once: chips, labels, the ticker, the
     tier cards and the balance. */
  paintSettlementLabels();
  renderCurrencyPicker();
  repaintRackAmounts();
  bal=0; renderBal();
  await refreshOnchainBalance();
  refreshDepBtn();
  toast(`Now playing in ${SETTLEMENT.label}`);
}

/* demo conversion rates — a live build would pull an oracle */
const RATES={USDG:1,PONS:0.03};
function tokenQty(){return depAmt/RATES[token];}
function refreshDepBtn(){
  if(isOnchainConfigured()){
    /* No demo conversion rate on-chain: the deposit is denominated in whatever
       the player picked in the asset row, and the contract settles in that
       same asset, so there is no second currency to show an equivalent in. */
    $("depConfirm").textContent=`Deposit ${money(depAmt)}`;
    $("tokEquiv").textContent="";
    return;
  }
  $("depConfirm").textContent=RATES[token]===1
    ?`Deposit $${depAmt.toLocaleString()} ${token}`
    :`Deposit $${depAmt.toLocaleString()} in ${token}`;
  $("tokEquiv").textContent=RATES[token]===1?""
    :`≈ ${tokenQty().toFixed(6)} ${token} at $${RATES[token].toLocaleString()} (demo rate)`;
}

/* Relabel the static markup for the settlement asset. The deposit chips and
   the wallet ticker are written as dollars in index.html for the demo build. */
function paintSettlementLabels(){
  const note=$("networkNote");
  if(!isOnchainConfigured()){
    if(note)note.textContent="Demo build · outcomes drawn locally · no real crypto, prizes or gambling involved";
    return;
  }
  /* This build settles real value on a live chain. Saying "testnet · no
     real-world value" here would be plainly false, and it is exactly the
     claim a player would rely on before depositing. */
  if(note)note.textContent=`Live on ${CHAIN_CONFIG.chainName} · settles in real ${SETTLEMENT.symbol} · test deployment, unaudited`;
  token=SETTLEMENT.display.symbol;
  $("tok").textContent=SETTLEMENT.display.symbol;
  $("sWon").textContent=money(0);
  $("sBest").textContent=money(0);
  /* The asset picker used to be hidden here, from when on-chain meant exactly
     one settlement asset and a chooser would have been a lie. There are three
     now, and this line was silently undoing renderCurrencyPicker() — the chips
     were built, labelled and wired, and then never shown. Leaving the row
     alone is the whole fix; hiding it is what was wrong. */
  document.querySelectorAll<HTMLElement>("#amtRow .chip[data-amt]").forEach(c=>{
    const v=Number(c.dataset.amt);
    if(v>0)c.textContent=money(v);
  });
  /* Name the unit in the free-text fields, so "Custom" cannot be read as
     dollars, ETH or anything else the labels are not showing. */
  /* Just the unit. "Custom (gwei)" clipped to "Custom (" in the 64px field,
     which named no unit at all — the opposite of the point. */
  const ph=SETTLEMENT.display.symbol;
  const dc=$("depCustom"), wc=$("wdCustom");
  if(dc)dc.placeholder=ph;
  if(wc)wc.placeholder=ph;
  document.querySelectorAll<HTMLElement>("#limitRow button[data-lim]").forEach(b=>{
    const v=Number(b.dataset.lim);
    if(v>0)b.textContent=money(v);
  });
}
$("tokRow").addEventListener("click",e=>{
  const c=(e.target as HTMLElement).closest(".chip") as HTMLElement|null;
  if(!c)return;
  if(c.dataset.cur){ audioInit(); void switchCurrency(c.dataset.cur); return; }
  /* legacy demo-token chips, only present when no contract is configured */
  if(!c.dataset.tok)return;
  [...$("tokRow").children].forEach(x=>x.classList.remove("sel")); c.classList.add("sel");
  token=c.dataset.tok; refreshDepBtn();
});
/* The main-screen row. Only ever carries real assets — the legacy demo-token
   chips above exist solely in the deposit sheet — so this needs none of that
   branch. */
$("assetRow").addEventListener("click",e=>{
  const c=(e.target as HTMLElement).closest(".chip") as HTMLElement|null;
  if(!c||!c.dataset.cur)return;
  audioInit();
  void switchCurrency(c.dataset.cur);
});
$("amtRow").addEventListener("click",e=>{
  const c=e.target.closest(".chip"); if(!c||!c.dataset.amt)return;
  [...$("amtRow").children].forEach(x=>x.classList.remove("sel")); c.classList.add("sel");
  $("depCustom").value="";
  depAmt=+c.dataset.amt; refreshDepBtn();
});
$("depCustom").addEventListener("input",e=>{
  const v=parseFloat(e.target.value);
  if(v>0){
    [...$("amtRow").children].forEach(x=>x.classList.remove("sel"));
    e.target.parentElement.classList.add("sel");
    /* What the player typed is in the denomination they can see (gwei
       on-chain), NOT internal display units — those differ by 1000x, so
       reading it raw would deposit a thousand times the intended amount. */
    const units=isOnchainConfigured()?displayToUnits(v):v;
    depAmt=Math.min(1e6,units); refreshDepBtn();
  }
});
$("depConfirm").onclick=()=>{
  if(depositing||!(depAmt>0))return;
  audioInit();
  /* ScratchyGame: deposit AND authorise the session key in one wallet
     confirmation, so every subsequent card needs no popup and no gas. */
  if(isOnchainConfigured()){
    /* Never fall through to the demo-credit branch below on a build that
       settles real value: it does `bal += depAmt`, which would show funds the
       contract does not hold and make every subsequent play fail at the
       relayer. Without a wallet the only honest answer is "connect one". */
    if(window.__onchain&&window.__onchain.provider()){ depositToGame(); return; }
    /* No wallet yet. Telling the player to connect one is a dead end when the
       thing that connects it is one tap away — take them there instead, and
       leave the deposit sheet open underneath so their chosen amount survives
       the round trip. */
    if(window.__onchain&&window.__onchain.connect){ window.__onchain.connect(); return; }
    $("depState").className="depstate warn";
    $("depState").textContent="Connect a wallet to deposit";
    return;
  }
  /* legacy MoonScratchVault path, kept while both contracts exist */
  if(window.__onchain&&window.__onchain.ready()){window.__onchain.deposit(depAmt);return;}
  depositing=true;
  const st=$("depState");
  st.textContent=`Adding demo credit…`;
  setTimeout(()=>{
    depositing=false;
    bal+=depAmt; renderBal(true); coinClink(8);
    window.__ref&&window.__ref.onDeposit(depAmt);
    st.textContent=RATES[token]===1
      ?`✓ $${fmt(depAmt)} demo ${token} credited`
      :`✓ ${tokenQty().toFixed(6)} demo ${token} ($${fmt(depAmt)}) credited`;
    unlockIfNeeded();
    setTimeout(closeDeposit,900);
  },700);
};
function unlockIfNeeded(){
  if(!dealtLocked||bal<tier.price)return;
  dealtLocked=false;

  if(isOnchainConfigured()){
    /* On-chain the contract charges and resolves. Both branches below are
       local-only: one deducts `bal` by hand, the other deals a card from the
       local RNG. Either would hand the player a card that never touched the
       chain while their deposit sat in the contract — the exact failure this
       whole path exists to remove. Just clear the lock; the next Play routes
       through dealOnchain(). */
    $("lock").classList.remove("on");
    return;
  }

  if(resolved){ /* demo card already played — buy a fresh one */
    newCard(true);
  }else{
    bal-=tier.price; renderBal();
    played++; $("sPlayed").textContent=played;
    window.__ref&&window.__ref.onPaidPlay(tier.price);
    $("lock").classList.remove("on");
  }
}

/* Both classes have to come off once the animation ends. Left on, #app keeps a
   transform and its own stacking context for the rest of the session, which
   quietly changes how fixed-position children behave. Re-adding on the next
   shake still needs the reflow, so the remove/offsetWidth dance stays. */
function clearShake(){const a=$("play");a.classList.remove("shake","megashake");}
function shakeFor(cls:string){
  /* #play, not #app: the card face reacts to the win while the controls below
     it stay exactly where the player is aiming. */
  const a=$("play");
  a.classList.remove("shake","megashake");
  void (a as HTMLElement).offsetWidth;
  a.classList.add(cls);
  a.addEventListener("animationend",clearShake,{once:true});
}
function shake(){shakeFor("shake");}
function megaShake(){shakeFor("megashake");}
let dispBal=0, balAnim=0;
function renderBal(bump?){
  const need=bal<(TIERS[0]?TIERS[0].price:1);
  $("depOpen").classList.toggle("need",need&&!demoSession);
  /* Demo cards never touch this balance. Showing a live on-chain figure next
     to a free card invites the reading that the demo win landed in it, so the
     chip is visibly stood down while demo play is on. */
  const w=$("wallet");
  w.classList.toggle("idle",demoSession);
  w.title=demoSession
    ? "Demo cards are free and do not use this balance"
    : (isOnchainConfigured()?`On-chain balance on ${CHAIN_CONFIG.chainName}`:"Demo credit");
  const from=dispBal, to=bal, id=++balAnim;
  dispBal=to;
  const ms=Math.abs(to-from)>50?900:350, t0=performance.now();
  (function step(t){
    if(id!==balAnim)return;
    const p=Math.min(1,(t-t0)/ms), e=1-Math.pow(1-p,3);
    /* fmt() is the 2dp dollar formatter; on-chain the balance is denominated
       like every other amount, or the wallet chip would read "0.00 gwei". */
    const v=from+(to-from)*e;
    $("bal").textContent=isOnchainConfigured()?formatUnits_(v):fmt(v);
    if(p<1)requestAnimationFrame(step);
  })(t0);
  /* Withdraw is only meaningful with an on-chain balance to move. */
  try{
    const wd=$("wdOpen");
    if(wd){
      wd.style.display=isOnchainConfigured()?"":"none";
      /* Left `disabled`, the tap never reached a handler and the only signal
         was 40% opacity on a dark pill — which QA read as a dead button. Keep
         it live so openWithdraw() can say why there is nothing to take out,
         and mark it aria-disabled for assistive tech. */
      wd.disabled=false;
      const empty=!(bal>0);
      wd.classList.toggle("empty",empty);
      wd.setAttribute("aria-disabled",String(empty));
      wd.title=empty?"Nothing to withdraw yet":"Withdraw to your wallet";
    }
  }catch(e){}
  if(bump){const w=$("wallet");w.classList.remove("bump");void w.offsetWidth;w.classList.add("bump");}
}

/* ======================= PARTICLES ======================= */
const fx=$("fx"), xctx=fx.getContext("2d");
function sizeFx(){fx.width=innerWidth;fx.height=innerHeight;} sizeFx();
addEventListener("resize",()=>{sizeFx();if(!resolved)drawFoil();});
let parts=[], shavings=[], fxOn=false, rainUntil=0;
const MAX_PARTS=900;
const CCOLORS=["#00D632","#7AFFB0","#FFFFFF","#26FF7A","#00A226","#B8FFD0"];
const SHADES=["#C7CCD6","#EDEFF4","#A9B0BD","#DDE1E8"];
/* Frames a flake rests at the foot of the card before fading. Long enough to
   read as debris piling up while you scratch, short enough that nothing is
   left behind once the card is done. */
const SETTLED_LIFE=600;
function ensureTick(){if(!fxOn)tickFx();}

function emitShavings(x,y,n){
  const pb=$("play").getBoundingClientRect();
  for(let i=0;i<n;i++){
    shavings.push({
      x:x+(Math.random()-.5)*10, y:y+(Math.random()-.5)*10,
      vx:(Math.random()-.5)*3.5, vy:-Math.random()*2.2-.6,
      g:.30+Math.random()*.1,
      rot:Math.random()*Math.PI*2, vr:(Math.random()-.5)*.5,
      w:Math.random()*7+3, h:Math.random()*2.5+1.2,
      curl:Math.random()<.55,
      c:SHADES[(Math.random()*SHADES.length)|0],
      floor:pb.bottom-4-Math.random()*6,
      minX:pb.left+6, maxX:pb.right-6,
      settled:false, blown:false, life:9999
    });
  }
  if(shavings.length>320)shavings.splice(0,shavings.length-320);
  ensureTick();
}
function sweepShavings(){
  shavings.forEach(s=>{
    s.settled=false; s.blown=true; s.life=55+Math.random()*30;
    s.vx=(Math.random()<.5?-1:1)*(3+Math.random()*8);
    s.vy=-Math.random()*5-2; s.g=.38; s.vr=(Math.random()-.5)*.7;
  });
}
function clearShavings(){shavings=[];}

function confetti(n){
  n=Math.min(n,MAX_PARTS-parts.length); if(n<=0)return;
  for(let i=0;i<n;i++){
    parts.push({t:"c",x:innerWidth/2+(Math.random()-.5)*220,y:innerHeight*.35,
      vx:(Math.random()-.5)*12,vy:-Math.random()*14-4,
      g:.32,rot:Math.random()*Math.PI,vr:(Math.random()-.5)*.3,
      s:Math.random()*7+4,c:CCOLORS[i%CCOLORS.length],life:110+Math.random()*50,coin:Math.random()<.25});
  }
  ensureTick();
}
function coinRain(ms){rainUntil=performance.now()+ms;ensureTick();}

function drawShaving(s){
  xctx.save();xctx.translate(s.x,s.y);xctx.rotate(s.rot);
  xctx.globalAlpha=(s.blown||s.settled)?Math.min(1,s.life/30):1;
  if(s.curl){
    xctx.strokeStyle=s.c;xctx.lineWidth=s.h;xctx.lineCap="round";
    xctx.beginPath();xctx.arc(0,0,s.w*.6,0.4,2.6);xctx.stroke();
    xctx.strokeStyle="rgba(255,255,255,.55)";xctx.lineWidth=s.h*.4;
    xctx.beginPath();xctx.arc(0,-s.h*.25,s.w*.6,0.5,2.4);xctx.stroke();
  }else{
    xctx.fillStyle=s.c;xctx.fillRect(-s.w/2,-s.h/2,s.w,s.h);
    xctx.fillStyle="rgba(255,255,255,.5)";xctx.fillRect(-s.w/2,-s.h/2,s.w,s.h*.4);
  }
  xctx.restore();
}
function tickFx(now?){
  fxOn=true; now=now||performance.now();
  if(now<rainUntil&&parts.length<MAX_PARTS&&Math.random()<.5){
    parts.push({t:"c",x:Math.random()*innerWidth,y:-20,vx:(Math.random()-.5)*2,
      vy:Math.random()*3+3,g:.12,rot:Math.random()*Math.PI,vr:(Math.random()-.5)*.25,
      s:Math.random()*8+6,c:"#FFC53D",life:200,coin:true});
  }
  xctx.clearRect(0,0,fx.width,fx.height);

  { let n=0; for(let i=0;i<shavings.length;i++){const s=shavings[i];
      if(s.life>0&&s.y<innerHeight+40)shavings[n++]=s;} shavings.length=n; }
  shavings.forEach(s=>{
    if(s.settled){
      /* Settled flakes age out too. They used to keep life:9999 forever, so
         the cull below never dropped them: the specks stayed on screen for
         the rest of the session AND kept this rAF loop alive permanently,
         redrawing static pixels. They are drawn on the fixed full-viewport
         #fx canvas, which is why they did not move when you scrolled. */
      s.life--;
    }else{
      s.x+=s.vx; s.y+=s.vy; s.vy+=s.g; s.rot+=s.vr;
      if(s.blown){s.life--;}
      else if(s.y>=s.floor){
        s.y=s.floor; s.vy=0; s.vx*=.4; s.vr*=.3;
        if(Math.abs(s.vx)<.15){s.settled=true;s.rot=(Math.random()-.5)*.6;s.life=SETTLED_LIFE;}
        s.x=Math.max(s.minX,Math.min(s.maxX,s.x));
      }
    }
    drawShaving(s);
  });

  { let n=0; for(let i=0;i<parts.length;i++){const p=parts[i];
      if(p.life>0&&p.y<innerHeight+40)parts[n++]=p;} parts.length=n; }
  parts.forEach(p=>{
    if(p.t==="w"){
      p.r+=p.vr; p.life--;
      xctx.save();
      xctx.globalAlpha=Math.max(0,p.life/26)*.9;
      xctx.strokeStyle="#7AFFB0"; xctx.lineWidth=3.5;
      xctx.beginPath(); xctx.arc(p.x,p.y,p.r,0,7); xctx.stroke();
      xctx.restore();
      return;
    }
    if(p.t==="s"){
      p.x+=p.vx; p.y+=p.vy; p.vy+=p.g; p.life--; p.vx*=.985; p.vy*=.985;
      xctx.save();
      xctx.globalAlpha=Math.min(1,p.life/24);
      xctx.strokeStyle=p.c; xctx.lineWidth=2.4; xctx.lineCap="round";
      const m=Math.hypot(p.vx,p.vy)||1, ux=p.vx/m, uy=p.vy/m;
      xctx.beginPath(); xctx.moveTo(p.x,p.y); xctx.lineTo(p.x-ux*p.len,p.y-uy*p.len); xctx.stroke();
      xctx.restore();
      return;
    }
    p.x+=p.vx;p.y+=p.vy;p.vy+=p.g;p.rot+=p.vr;p.life--;p.vx*=.99;
    xctx.save();xctx.translate(p.x,p.y);xctx.rotate(p.rot);
    xctx.globalAlpha=Math.min(1,p.life/40);
    if(p.coin){
      const g=xctx.createRadialGradient(-p.s*.3,-p.s*.3,1,0,0,p.s);
      g.addColorStop(0,"#D9FFE6");g.addColorStop(1,"#00A226");
      xctx.fillStyle=g;xctx.beginPath();xctx.arc(0,0,p.s,0,7);xctx.fill();
      xctx.fillStyle="rgba(2,60,18,.6)";xctx.font=`900 ${p.s}px Nunito`;
      xctx.textAlign="center";xctx.textBaseline="middle";xctx.fillText("$",0,1);
    }else{xctx.fillStyle=p.c;xctx.fillRect(-p.s/2,-p.s/2,p.s,p.s*.6);}
    xctx.restore();
  });

  if(!parts.length&&!shavings.length&&now>=rainUntil){fxOn=false;return;}
  requestAnimationFrame(tickFx);
}

/* ================= LIVE STATS ================= */
/* Client spec: only genuine activity, never fake winners or statistics.
   Counts real scratches: this session, plus a real cross-user total when
   shared storage exists. Post-launch this wires to CardResolved logs. */
let totalN=0, statsShared=false;
function paintLive(){
  $("totalScratch").textContent=totalN.toLocaleString();
}
async function liveInit(){
  try{
    if(window.storage){
      const r=await window.storage.get('scratchy-global-stats',true).catch(()=>null);
      if(r&&r.value){const v=JSON.parse(r.value);totalN=v.total||0;}
      statsShared=true;
    }
  }catch(e){}
  if(!statsShared)totalN=saved.scratched;
  paintLive();
}
function bumpTotal(){
  totalN++; paintLive(); persistState();
  if(statsShared&&window.storage)window.storage.set('scratchy-global-stats',JSON.stringify({total:totalN}),true).catch(()=>{});
}

/* MetaMask can sit on any chain. Every on-chain action below must confirm it
   is on the one the contract is deployed to first — otherwise calls land on an
   address with no code there and fail as "missing revert data", which tells
   the player nothing. */
async function ensureCorrectChain(){
  const p=window.__onchain&&window.__onchain.provider&&window.__onchain.provider();
  if(!p)return false;
  try{
    const id=await p.request({method:"eth_chainId"});
    if(String(id).toLowerCase()===CHAIN_CONFIG.chainId.toLowerCase())return true;
    return await window.__onchain.switchChain();
  }catch(e){ return false; }
}

async function depositToGame(){
  const st=$("depState");
  try{
    depositing=true;
    st.textContent="Checking network…";
    if(!await ensureCorrectChain()){
      st.className="depstate warn";
      st.textContent=`Switch your wallet to ${CHAIN_CONFIG.chainName} to deposit`;
      depositing=false; return;
    }
    st.className="depstate";
    st.textContent="Confirm the deposit in your wallet…";
    /* Over WalletConnect that prompt is in another app. Take them to it. */
    window.__onchain&&window.__onchain.nudge&&window.__onchain.nudge();
    const { depositAndAuthorise }=await import("./chain/game");
    await depositAndAuthorise(window.__onchain.provider(),depAmt);
    st.className="depstate";
    st.textContent="✓ Deposited and session authorised";
    await refreshOnchainBalance();
    window.__ref&&window.__ref.onDeposit(depAmt);
    unlockIfNeeded(); setTimeout(closeDeposit,900);
  }catch(e){
    const { explainChainError }=await import("./chain/game");
    st.className="depstate err";
    st.textContent=explainChainError(e);
  }finally{ depositing=false; }
}

/* ================= WITHDRAWAL FLOW =================
   Deliberately a wallet-confirmed transaction, unlike play. Money leaving the
   contract should always be an explicit act the player signs for — the session
   key exists to make PLAYING frictionless, and it is scoped so it can never
   move funds out. */
let wdPct=100, wdAmount=0, withdrawing=false;

function wdMax(){ return bal; }

function refreshWithdraw(){
  const max=wdMax();
  wdAmount = wdPct===null ? wdAmount : (max*wdPct)/100;
  if(wdAmount>max)wdAmount=max;
  $("wdAvail").textContent=`Available: ${money(max)}`;
  const btn=$("wdConfirm");
  btn.textContent = wdAmount>0 ? `Withdraw ${money(wdAmount)}` : "Nothing to withdraw";
  btn.disabled = !(wdAmount>0) || withdrawing;
}

function openWithdraw(){
  if(!isOnchainConfigured()){
    toast("Withdrawals need an on-chain balance");
    return;
  }
  /* Silently doing nothing reads as a broken button. */
  if(!(wdMax()>0)){
    toast(bal>0?"Nothing to withdraw yet — that balance is demo credit"
               :"Nothing to withdraw — your balance is 0");
    return;
  }
  wdPct=100; wdAmount=wdMax();
  $("wdState").textContent="";
  document.querySelectorAll<HTMLElement>("#wdRow .chip[data-pct]").forEach(c=>
    c.classList.toggle("sel", Number(c.dataset.pct)===100));
  ($("wdCustom") as HTMLInputElement).value="";
  refreshWithdraw();
  $("withdrawModal").classList.add("on");
}
function closeWithdraw(){ $("withdrawModal").classList.remove("on"); }

$("wdOpen").onclick=()=>{ audioInit(); openWithdraw(); };
$("wdClose").onclick=closeWithdraw;
$("withdrawModal").addEventListener("click",e=>{ if((e.target as HTMLElement).id==="withdrawModal")closeWithdraw(); });

$("wdRow").addEventListener("click",e=>{
  const c=(e.target as HTMLElement).closest(".chip") as HTMLElement|null;
  if(!c||!c.dataset.pct)return;
  document.querySelectorAll<HTMLElement>("#wdRow .chip").forEach(x=>x.classList.remove("sel"));
  c.classList.add("sel");
  ($("wdCustom") as HTMLInputElement).value="";
  wdPct=Number(c.dataset.pct);
  refreshWithdraw();
});

$("wdCustom").addEventListener("input",e=>{
  const v=parseFloat((e.target as HTMLInputElement).value);
  document.querySelectorAll<HTMLElement>("#wdRow .chip").forEach(x=>x.classList.remove("sel"));
  (e.target as HTMLElement).parentElement!.classList.add("sel");
  wdPct=null as any;
  /* Typed in the displayed denomination, same as the deposit field. */
  const units = v>0 ? (isOnchainConfigured()?displayToUnits(v):v) : 0;
  wdAmount = Math.min(units, wdMax());
  refreshWithdraw();
});

$("wdConfirm").onclick=async()=>{
  if(withdrawing||!(wdAmount>0))return;
  audioInit();
  const st=$("wdState");
  withdrawing=true; refreshWithdraw();
  try{
    st.textContent="Checking network…";
    if(!await ensureCorrectChain()){
      st.className="depstate warn";
      st.textContent=`Switch your wallet to ${CHAIN_CONFIG.chainName} to withdraw`;
      return;
    }
    st.textContent="Confirm the withdrawal in your wallet…";
    window.__onchain&&window.__onchain.nudge&&window.__onchain.nudge();
    const { withdraw }=await import("./chain/game");
    const hash=await withdraw(window.__onchain.provider(), wdAmount);
    st.className="depstate";
    st.textContent=`✓ Withdrawn ${money(wdAmount)}`;
    window.__lastWithdraw=hash;
    await refreshOnchainBalance();
    setTimeout(closeWithdraw,1200);
  }catch(e){
    const { explainChainError }=await import("./chain/game");
    st.className="depstate err";
    st.textContent=explainChainError(e);
  }finally{
    withdrawing=false; refreshWithdraw();
  }
};

/* ================= SETTLEMENT RECEIPT =================
   Was a local commit-reveal demo. On-chain it shows the real thing: the tx that
   settled the card, both randomness shares and the combined value the contract
   used. This is an AUDIT TRAIL, not a proof — the operator supplies its share
   at submission time, so a result cannot be verified in advance, only
   reconstructed afterwards. The panel copy in index.html says exactly that;
   keep the two in step. */
function showFairness(res:{txHash:string;playerSeed:string;externalRandomness:string;randomness:string}){
  try{
    $("fairTag").textContent="🧾 "+res.txHash.slice(2,10).toUpperCase();
    $("fairCommit").textContent=res.randomness;
    $("fairReveal").style.display="block";
    $("fairLayoutTxt").textContent=res.playerSeed;
    $("fairSaltTxt").textContent=res.externalRandomness;
    const link=CHAIN_CONFIG.explorer?`${CHAIN_CONFIG.explorer}/tx/${res.txHash}`:res.txHash;
    $("fairVerify").textContent=link;
  }catch(e){}
}

/* ---- local demo commit-reveal, used only when no contract is configured ---- */
let fairSalt="", fairHash=null, fairLayout="";
async function sha256hex(str){
  const h=await crypto.subtle.digest("SHA-256",new TextEncoder().encode(str));
  return [...new Uint8Array(h)].map(x=>x.toString(16).padStart(2,"0")).join("");
}
function armFairness(){
  fairLayout=cells.map(c=>c.id).join(",");
  fairSalt=[...crypto.getRandomValues(new Uint8Array(16))].map(x=>x.toString(16).padStart(2,"0")).join("");
  fairHash=null;
  $("fairTag").textContent="🛡 FAIR …";
  sha256hex(fairLayout+"|"+fairSalt)
    .then(h=>{fairHash=h;$("fairTag").textContent="🛡 FAIR "+h.slice(0,8).toUpperCase();})
    .catch(()=>{$("fairTag").textContent="🛡 FAIR N/A";});
}
$("fairTag").onclick=async()=>{
  $("fairCommit").textContent=fairHash||"computing…";
  if(resolved&&fairHash){
    $("fairReveal").style.display="block";
    $("fairLayoutTxt").textContent=fairLayout;
    $("fairSaltTxt").textContent=fairSalt;
    try{
      const h=await sha256hex(fairLayout+"|"+fairSalt);
      $("fairVerify").textContent=h===fairHash?"✓ MATCH. Layout was committed before you scratched":"✗ MISMATCH";
    }catch(e){$("fairVerify").textContent="verification unavailable in this context";}
  }else{
    $("fairReveal").style.display="none";
  }
  $("fairModal").classList.add("on");
};
$("fairClose").onclick=()=>$("fairModal").classList.remove("on");
$("fairModal").addEventListener("click",e=>{if(e.target.id==="fairModal")$("fairModal").classList.remove("on");});

/* ================= ONBOARDING ================= */
function startDemo(){
  onboardingDemo=true; /* free card, exact same odds as paid play */
  saved.freeCardUsed=true; persistState();
  dealDemo();
}
$("wDemo").onclick=()=>{audioInit();$("welcome").classList.remove("on");startDemo();};
$("wFund").onclick=()=>{audioInit();$("welcome").classList.remove("on");openDeposit();};

newCard(false); renderBal(); showLock(); liveInit();
/* SELF-TEST: verify the tension/win machinery exists and its CSS is live in
   THIS browser, then wear the result in the header. Green chip = the beam,
   heartbeat, and celebration code is present and renderable on this device. */
setTimeout(()=>{
  const fails=[];
  try{
    [["updateTension",updateTension],["tensionStart",tensionStart],["positionBeam",positionBeam],
     ["gigaScratch",gigaScratch],["finish",finish],["checkCells",checkCells]].forEach(([n,f])=>{
      if(typeof f!=="function")fails.push(n);
    });
    const probe=document.createElement("div");
    probe.className="tension-bar on"; probe.style.position="absolute"; probe.style.visibility="hidden";
    document.body.appendChild(probe);
    if(getComputedStyle(probe).display==="none")fails.push("tension-bar CSS");
    probe.className="cell hot";
    if(getComputedStyle(probe).animationName==="none")fails.push("heartbeat CSS");
    probe.remove();
    if(!fctx||!foil.width)fails.push("canvas");
  }catch(e){fails.push("selftest: "+e.message);}
  const chip=$("verchip");
  if(fails.length){chip.textContent=BUILD_ID+" ✗ "+fails[0];chip.className="verchip bad";
    console.error("SELF-TEST FAILED:",fails);}
  else{chip.textContent=BUILD_ID+" ✓";chip.className="verchip ok";}
},400);
try{$("buildStamp").textContent="build "+BUILD_ID;}catch(e){}
function renderPnl(){
  try{
    const spent=played*0+spentSession, back=totalWon;
    const net=back-spent;
    $("pnlRow").innerHTML=`<span>cards <b>${played}</b></span>`+
      `<span>staked <b>${money(spent)}</b></span>`+
      `<span>returned <b>${money(back)}</b></span>`+
      `<span>net <b class="${net>=0?"up":"dn"}">${net>=0?"+":"−"}${money(Math.abs(net))}</b></span>`;
  }catch(e){}
}
function renderLimit(){
  try{
    $("limState").textContent=spendLimit===null?"no limit set"
      :`${money(spentSession)} of ${money(spendLimit)} used`;
    document.querySelectorAll<HTMLButtonElement>(".limitrow button").forEach(b=>{
      b.classList.toggle("sel",(b.dataset.lim==="off"&&spendLimit===null)||Number(b.dataset.lim)===spendLimit);
    });
  }catch(e){}
}
try{document.querySelectorAll<HTMLButtonElement>(".limitrow button").forEach(b=>{
  b.onclick=()=>{
    const v=b.dataset.lim==="off"?null:Number(b.dataset.lim);
    if(spendLimit!==null&&(v===null||v>spendLimit)){
      toast("Limit increases apply next session"); return;   /* RG: no impulse raises */
    }
    spendLimit=v; renderLimit(); persistState();
    toast(v===null?"Session limit off":`Session limit set: ${money(v)}`);
  };
}); renderLimit(); renderPnl();}catch(e){}
paintSettlementLabels();
renderCurrencyPicker();
/* Shown once per player, not once per page load. Re-offering "try a free
   scratch" on every visit was both a nag and a false promise: the free card is
   meant to be a one-off, and QA reported it re-offered indefinitely. */
if(!saved.welcomeSeen){
  $("welcome").classList.add("on");
  saved.welcomeSeen=true;
  persistState();
}else if(saved.freeCardUsed){
  /* Nothing to introduce and no free card left — go straight to the game. */
}

/* ================= ODDS TRANSPARENCY =================
   Full paytable, RTP and win frequency, stated plainly — including the
   share of wins that only return the stake. Certifiable, and the thing
   that separates a trustworthy card from a scammy one. */
/**
 * "1 in N", with enough precision to audit.
 *
 * Rounding every row to a whole number looks tidier but does not add up: a QA
 * pass reconstructed the paytable from this column and got 89.6% RTP against
 * the 90.9% printed two lines below it. The gap is all rounding — 1 in 5.556
 * shown as "1 in 6" is a 7% error on that row — but a table that invites
 * auditing has to survive being audited. One decimal under 100 closes it.
 */
function oddsStr(prob:number):string{
  const n=1/prob;
  if(n<100)return n.toFixed(2);
  if(n<1000)return n.toFixed(1);
  return Math.round(n).toLocaleString();
}

function renderOdds(){
  const t=tier;
  $("oddsSub").textContent=`${t.short} · ${money(t.price)} a card`;
  const rows=t.prizes.map((pr,i)=>{
    const [amt,prob]=pr, sym=ALLSYMS[i];
    const mult=amt/t.price;
    const cls=sym.type==="moon"?"moonrow":(mult===1?"evenrow":"");
    return `<tr class="${cls}"><td>${NAMES[sym.id]}</td><td>${money(amt)}${mult===1?" <span style='color:#8E8E93'>(stake back)</span>":mult<1?" <span style='color:#8A6A3D'>(partial return)</span>":""}</td><td>1 in ${oddsStr(prob)}</td></tr>`;
  }).join("");
  $("oddsTable").innerHTML=`<tr><th>MATCH 3 OF</th><th style="text-align:right">PAYS</th><th style="text-align:right">CHANCE</th></tr>${rows}`;
  const wr=t.prizes.reduce((a,[,p])=>a+p,0);
  const ev=t.prizes.reduce((a,[x,p])=>a+x*p,0);
  const evenProb=t.prizes.filter(([x])=>x===t.price).reduce((a,[,p])=>a+p,0);
  const partProb=t.prizes.filter(([x])=>x<t.price).reduce((a,[,p])=>a+p,0);
  const profitProb=wr-evenProb-partProb;
  $("oddsKeys").innerHTML=
    `<div class="okey"><span>Card pays anything at all</span><b>1 in ${(1/wr).toFixed(1)}</b></div>`+
    `<div class="okey"><span>A win that beats the card price</span><b>1 in ${(1/profitProb).toFixed(1)}</b></div>`+
    `<div class="okey"><span>House comes out ahead</span><b>${((1-wr+partProb)*100).toFixed(0)}% of cards</b></div>`+
    `<div class="okey"><span>Return to player (RTP)</span><b>${(ev/t.price*100).toFixed(1)}%</b></div>`+
    `<div class="okey"><span>Top prize</span><b>${money(t.top)}</b></div>`;
  $("oddsNote").innerHTML=
    `<b>Most payouts are not wins.</b> ${Math.round(partProb/wr*100)}% of paying cards return less than the card price and ${Math.round(evenProb/wr*100)}% return exactly the card price. Scratchy labels those as partial returns and covered cards — never as wins.<br><br>`+
    `RTP is the long-run average returned across all cards; a single card returns nothing or a prize. `+
    (ev/t.price>=1?`This demo table is player-positive (${(ev/t.price*100).toFixed(1)}%) for demonstration and is not a launch table.`:``);
}
$("oddsLink").onclick=()=>{audioInit();renderOdds();$("oddsModal").classList.add("on");};
$("oddsClose").onclick=()=>$("oddsModal").classList.remove("on");
$("oddsModal").addEventListener("click",e=>{if(e.target.id==="oddsModal")$("oddsModal").classList.remove("on");});


/* ---- interface consumed by chain.ts (deposits) and demo.ts ---- */
export { renderBal, unlockIfNeeded, closeDeposit, newCard, checkCells, finish, gigaScratch };
export const setBal=(v)=>{bal=Number(v)||0;};
export const setDepositing=(v)=>{depositing=!!v;};
export const getBal=()=>bal;
export const currentTier=()=>tier;
export const getWinSet=()=>winSet;
export const isResolved=()=>resolved;
export const gigaRunning=()=>gigaOn;
export const foilCanvas=()=>({foil,fctx,rects,dpr,revealedCells});
export const debugSnapshot=()=>({BUILD_ID,revealed:[...revealedCells],win:winSet?[...winSet]:null,prize,resolved});


/* ============ play-session renewal ============
   The browser session key can only spend what the player authorised, for as
   long as they authorised it. Running out is the design working, not a fault —
   but it does need a one-tap way back in. */
let sePct=50, seBusy=false;

function renderSession(){
  const avail=$("seAvail"), row=$("seRow");
  avail.textContent=`Balance: ${money(bal)}`;
  [...row.querySelectorAll(".chip")].forEach(c=>
    c.classList.toggle("sel",Number((c as HTMLElement).dataset.units)===sePct));
  const units=bal*sePct/100;
  /* A disabled button with no explanation is its own dead end. With nothing to
     authorise, the honest action is to offer the thing that fixes that. */
  $("seConfirm").textContent=units>0?`Approve ${money(units)}`:"Add funds first";
  ($("seConfirm") as HTMLButtonElement).disabled=seBusy;
}

function openSession(msg?){
  sePct=50; seBusy=false;
  $("seState").className="depstate";
  $("seState").textContent=msg||"";
  renderSession();
  $("sessionModal").classList.add("on");
}
function closeSession(){ $("sessionModal").classList.remove("on"); }

$("seClose").onclick=closeSession;
$("sessionModal").addEventListener("click",e=>{ if(e.target===$("sessionModal"))closeSession(); });
$("seRow").addEventListener("click",e=>{
  const c=(e.target as HTMLElement).closest(".chip") as HTMLElement|null;
  if(!c||!c.dataset.units)return;
  sePct=Number(c.dataset.units); renderSession();
});

$("seConfirm").onclick=async()=>{
  if(seBusy)return;
  const prov=window.__onchain&&window.__onchain.provider&&window.__onchain.provider();
  if(!prov){ window.__onchain&&window.__onchain.connect&&window.__onchain.connect(); return; }
  const units=bal*sePct/100;
  if(!(units>0)){ closeSession(); openDeposit("Add funds, and the session renews with them"); return; }
  seBusy=true; renderSession();
  const st=$("seState");
  st.className="depstate";
  st.textContent="Confirm in your wallet…";
  /* Over WalletConnect that prompt is in another app entirely. */
  window.__onchain&&window.__onchain.nudge&&window.__onchain.nudge();
  try{
    await reauthoriseSession(prov,units);
    st.className="depstate";
    st.textContent="✓ Session renewed — deal away";
    setTimeout(closeSession,900);
  }catch(e){
    st.className="depstate err";
    st.textContent=explainChainError(e);
  }finally{
    seBusy=false; renderSession();
  }
};

/* Reachable before it breaks, not only after: the wallet chip offers it once
   a session exists, so a player can extend one they can see running down. */
export { openSession };
